import { defaultChartFor } from '../../utils/analytics';
import { getCollectionMemberCardIds } from '../../utils/collections';
import { normalizeId } from '../../utils/ids';
import { accessibleCardIds, isCardScoped } from '../../utils/permissions';
import { cardAndDescendantIds } from '../../utils/facets';
import { buildMemberCardOptions } from '../cards/CardVisuals';
import { cardElementChain, chartSpecForDisplay } from '../../utils/chartSpecFields';
import { cardLabel, chartFields, emptyDraft, newGroup, newMeasure, reconcile, specFromDraft, suggestName } from './builderModel';

/**
 * SUGGESTED CHARTS (charts A10; keepr-web docs/plans/analytics.md §6.1):
 * starting points made from a collection's cards by the vendored capability
 * table, offered on Reports — as the empty state's suggestions, and as a short
 * list after the charts once there are some. Nothing here runs a chart: a
 * suggestion is text (its name, its card, its form), as the Reports rows are,
 * and choosing one opens the builder pre-filled and unsaved.
 *
 * For each card, at most three, in this order — each only where the table
 * offers it:
 *
 *   over-time   the card's first quantity (a number, an amount of money or a
 *               measurement — never auto-numbering) by its first date, or by
 *               the date an item was added when it has none: the measure and
 *               the bucket the table's own default proposes
 *               (`defaultChartFor`: a total by month; an average by day for a
 *               measurement that never totals, like a temperature)
 *   by-choice   a count of items by the card's first choice
 *   by-month    a count of items by month of the card's first date (or of
 *               the date an item was added)
 *
 * Cards are read in the card picker's order (the collection's member cards,
 * parents first, narrowed to a card-scoped reader's own), and the list is
 * filled a round at a time — every card's first, then every card's second —
 * so a collection of many cards offers each card before any card twice. A
 * suggestion the collection already has as a chart (the same card, measure
 * and grouping) is not offered. Deterministic: the same cards always give the
 * same list, which is what lets the builder rebuild one from its key alone.
 */

export const SUGGESTION_LIMITS = Object.freeze({ empty: 6, strip: 3 });

const KINDS = Object.freeze(['over-time', 'by-choice', 'by-month']);

const has = (list, value) => Array.isArray(list) && list.indexOf(value) !== -1;
const quantityKinds = ['number', 'money', 'measurement'];

/** The cards a chart here may count (the builder's own rule): the collection's family, narrowed to a card-scoped reader's cards. */
export function chartableCardIds(collection, cards, familyIds) {
	if (collection && isCardScoped(collection)) {
		const mine = new Set(accessibleCardIds(collection));
		return familyIds.size ? new Set([...familyIds].filter((id) => mine.has(id))) : mine;
	}
	return familyIds;
}

/** The cards suggestions are made from, in the card picker's order: the collection's own member cards a chart may count. */
export function suggestionCards(collection, cards, familyIds) {
	if (!collection) return [];
	const members = getCollectionMemberCardIds(collection, cards);
	const chartable = chartableCardIds(collection, cards, familyIds);
	const usable = new Set([...members].filter((id) => chartable.has(id)));
	return buildMemberCardOptions(cards, usable).map(({ card }) => card);
}

/** The key the builder is handed (`?suggestion=`): the kind and the card. */
export const suggestionKey = (kind, cardId) => `${kind}.${cardId}`;

/** → { kind, cardId } or null for a key that is not one. */
export function parseSuggestionKey(key) {
	if (typeof key !== 'string') return null;
	const at = key.lastIndexOf('.');
	if (at < 1) return null;
	const kind = key.slice(0, at);
	const cardId = normalizeId(key.slice(at + 1));
	if (!has(KINDS, kind) || !cardId || !/^[0-9a-f]{24}$/i.test(cardId)) return null;
	return { kind, cardId };
}

/** What makes two charts "the same suggestion": the card, each measure's op and element, and the grouping. */
export function chartSignature(spec) {
	const s = spec && typeof spec === 'object' ? spec : {};
	const group = s.groupBy && typeof s.groupBy === 'object' ? { on: s.groupBy.on || null, bucket: s.groupBy.bucket || null } : null;
	return JSON.stringify({
		card: s.card_id || null,
		measures: (Array.isArray(s.measures) ? s.measures : []).map((m) => [m && m.op, (m && m.element) || null]),
		group,
	});
}

/** The chart's world for one card, as the builder builds it. */
function worldOf(card, { getElementsForCard, cards, familyIds, hasSubCollections }) {
	const cardId = normalizeId(card && card._id);
	const elements = cardId && typeof getElementsForCard === 'function' ? (getElementsForCard(cardId) || []).filter(Boolean) : [];
	const descendants = cardId && Array.isArray(cards) ? cardAndDescendantIds(cards, cardId).filter((id) => id !== cardId && familyIds.has(id)) : [];
	return {
		card,
		cardName: cardLabel(card),
		fields: chartFields({ card, elements, cardHasDescendants: descendants.length > 0, hasSubCollections: Boolean(hasSubCollections) }),
		hasSubCollections: Boolean(hasSubCollections),
		refusedElements: {},
	};
}

const usable = (f) => f && !f.reserved && f.description;
const groupKind = (f) => (f.description.groups && f.description.groups[0] ? f.description.groups[0].kind : null);

/** The date a suggestion groups by: the card's first date element, else the date an item was added. */
function dateFieldOf(ctx) {
	const own = ctx.fields.find((f) => usable(f) && !f.system && f.description.time === true && groupKind(f) === 'time');
	return own || ctx.fields.find((f) => f.name === 'created') || null;
}

/** A group on `field` at `bucket` when the field offers it, else at its own default. */
function groupAt(field, bucket) {
	const group = newGroup(field);
	const g = field.description.groups && field.description.groups[0];
	if (bucket && g && Array.isArray(g.buckets) && has(g.buckets, bucket)) group.bucket = bucket;
	return group;
}

/** One suggestion's draft for a card, or null where the table offers none. */
function draftFor(kind, ctx) {
	const cardId = normalizeId(ctx.card && ctx.card._id);
	const dateField = dateFieldOf(ctx);
	const base = { ...emptyDraft(), cardId };
	if (kind === 'over-time') {
		const quantity = ctx.fields.find((f) => usable(f) && !f.system && has(quantityKinds, f.description.kind));
		if (!quantity || !dateField) return null;
		const proposal = defaultChartFor(quantity.description);
		const measure = proposal && proposal.measures && proposal.measures[0];
		if (!measure || !measure.op || measure.op === 'count' || !has(quantity.description.measures, measure.op)) return null;
		const bucket = proposal.groupBy && proposal.groupBy.bucket;
		return { ...base, measures: [{ ...newMeasure([], measure.op), element: quantity.name }], groupBy: groupAt(dateField, bucket) };
	}
	if (kind === 'by-choice') {
		const choice = ctx.fields.find((f) => usable(f) && !f.system && groupKind(f) === 'choice');
		if (!choice) return null;
		return { ...base, groupBy: newGroup(choice) };
	}
	if (kind === 'by-month') {
		if (!dateField) return null;
		return { ...base, groupBy: groupAt(dateField, 'month') };
	}
	return null;
}

/** A suggestion, settled as the builder would settle it: the recommended form, the spec, the name. */
function settle(kind, card, ctx) {
	const raw = draftFor(kind, ctx);
	if (!raw) return null;
	const { draft } = reconcile(raw, ctx);
	const spec = specFromDraft(draft, ctx);
	return {
		key: suggestionKey(kind, normalizeId(card._id)),
		kind,
		cardId: normalizeId(card._id),
		card,
		name: suggestName(draft, ctx),
		form: draft.show.type,
		draft,
		spec,
	};
}

/**
 * suggestCharts(world) → the suggestions, at most `limit`.
 *
 *   cards              the cards to suggest from, in order (suggestionCards)
 *   getElementsForCard the card cache's merged elements of a card
 *   allCards           every card the cache holds (for descendants)
 *   familyIds          the cards a chart here may count
 *   hasSubCollections  whether the collection has sub-collections
 *   existing           the specs of the charts the collection already shows
 *   limit              how many
 */
export function suggestCharts({ cards = [], getElementsForCard, allCards = [], familyIds = new Set(), hasSubCollections = false, existing = [], limit = SUGGESTION_LIMITS.empty } = {}) {
	// A chart's fields may name its elements by id (KPR-238 P6), and a
	// suggestion's by id or by name: both are compared by the elements' names.
	const byId = Object.fromEntries((allCards || []).filter(Boolean).map(c => [normalizeId(c._id), c]));
	const named = (spec) => {
		const cardId = normalizeId(spec && spec.card_id);
		const list = cardId && typeof getElementsForCard === 'function' ? (getElementsForCard(cardId) || []).filter(Boolean) : [];
		return chartSignature(chartSpecForDisplay(spec, list, cardId ? cardElementChain(cardId, byId) : []));
	};
	const taken = new Set((existing || []).map(named));
	const perCard = (cards || []).filter(Boolean).map((card) => {
		const ctx = worldOf(card, { getElementsForCard, cards: allCards, familyIds, hasSubCollections });
		return KINDS.map((kind) => settle(kind, card, ctx)).filter((s) => s && !taken.has(named(s.spec)));
	});
	const out = [];
	for (let round = 0; round < KINDS.length && out.length < limit; round += 1) {
		for (let i = 0; i < perCard.length && out.length < limit; i += 1) {
			if (perCard[i][round]) out.push(perCard[i][round]);
		}
	}
	return out;
}

/**
 * The one suggestion a key names, rebuilt from the card it names (the
 * builder's pre-fill), or null when that card is not one suggestions are made
 * from here, or the table no longer offers that kind for it.
 */
export function suggestionFor(key, world) {
	const parsed = parseSuggestionKey(key);
	if (!parsed) return null;
	const card = (world.cards || []).find((c) => normalizeId(c && c._id) === parsed.cardId);
	if (!card) return null;
	const ctx = worldOf(card, { getElementsForCard: world.getElementsForCard, cards: world.allCards || [], familyIds: world.familyIds || new Set(), hasSubCollections: world.hasSubCollections });
	return settle(parsed.kind, card, ctx);
}
